'use client';

// Imperative toast, `toast.success/error/warning/info('msg', optionalBody)` from
// anywhere, no provider/context threading. A module-level pub/sub feeds a single
// <Toaster/> mounted in the layout.
//
// Compact chip style: flat bgSub chip, 0.5px border, borderRadius 5, no left
// accent, no box-shadow. Icon colour-coded per type, text in ink2. Success/info
// auto-dismiss with a hairline countdown; errors and warnings PERSIST until
// dismissed.

import React, { useState, useEffect, useCallback } from 'react';
import { HV2, modalRadius } from './lib';

let _id = 0;
const listeners = new Set();
function emit(message, type, body, action) {
  if (!message) return;
  const t = { id: ++_id, message: String(message), type: type || 'info', body: body ? String(body) : '', action: action && action.label && action.onAction ? action : null };
  listeners.forEach((fn) => fn(t));
}

export const toast = {
  success: (m, body) => emit(m, 'success', body),
  error: (m, body) => emit(m, 'error', body),
  warning: (m, body) => emit(m, 'warning', body),
  info: (m, body) => emit(m, 'info', body),
  // Melding met EEN knop erin, voor een handeling die alleen nu nog zin heeft:
  // 'Ongedaan maken' na een bulkactie op een bellijst. `action` is
  // { label, onAction, ms }; ms verlengt het venster (standaard 8 seconden, want
  // een besluit om iets terug te draaien duurt langer dan een melding lezen).
  // De knop sluit de melding zelf, dus een tweede klik kan niet.
  action: (m, action, body) => emit(m, 'info', body, action),
};

const KIND = {
  success: { fg: '#2e7d4f', icon: <path d="M20 6 9 17l-5-5" /> },
  error: { fg: '#b1303f', icon: <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></> },
  warning: { fg: '#a85a13', icon: <><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></> },
  info: { fg: HV2.blue, icon: <><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></> },
};
// Auto-dismiss windows. error/warning are intentionally absent → they persist
// until the user dismisses them.
const AUTO = { success: 4500, info: 4500 };
// Een melding met een knop erin krijgt langer: standaard 8 seconden, of wat de
// aanroeper meegeeft. Eén functie, zodat de balk in de kaart en de opruimtimer
// in de Toaster nooit uit elkaar kunnen lopen.
const autoMs = (t) => (t.action ? (Number(t.action.ms) || 8000) : AUTO[t.type]);

// Phone breakpoint, matches the app-wide isMobile threshold (innerWidth < 768).
// This component mounts standalone in the layout (no isMobile prop reaches it),
// so it tracks the media query itself. SSR-safe: state starts false (desktop),
// the real value lands after mount.
function useIsMobileViewport() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 767px)');
    setIsMobile(mql.matches);
    const onChange = (e) => setIsMobile(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return isMobile;
}

function ToastCard({ t, onClose, isMobile }) {
  const k = KIND[t.type] || KIND.info;
  const auto = autoMs(t);
  return (
    <div role={t.type === 'error' || t.type === 'warning' ? 'alert' : 'status'} style={{
      display: 'inline-flex', alignItems: 'center', gap: 7, maxWidth: isMobile ? 'none' : '92vw', width: isMobile ? '100%' : 'auto', boxSizing: 'border-box',
      padding: '6px 10px', borderRadius: modalRadius, background: HV2.bgSub,
      border: `0.5px solid ${HV2.border}`, pointerEvents: 'auto',
      position: 'relative', animation: 'pa-toast-in 0.2s ease-out',
    }}>
      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke={k.fg} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>{k.icon}</svg>
      <span style={{ fontSize: HV2.fsText, fontWeight: 480, color: HV2.ink2, whiteSpace: isMobile ? 'normal' : 'nowrap' }}>{t.message}</span>
      {t.body && <span style={{ fontSize: HV2.fsText, color: HV2.muted, whiteSpace: isMobile ? 'normal' : 'nowrap' }}>{t.body}</span>}
      {t.action && (
        <button type="button" onClick={() => { onClose(); t.action.onAction(); }}
          style={{ border: 'none', background: 'transparent', padding: 0, fontFamily: 'inherit', fontSize: HV2.fsText, fontWeight: 650, color: HV2.blue, cursor: 'pointer', whiteSpace: 'nowrap', marginLeft: 2 }}>
          {t.action.label}
        </button>
      )}
      <button onClick={onClose} aria-label="Sluiten" style={{
        border: 'none', background: 'transparent', cursor: 'pointer', color: HV2.mute2, fontSize: 14, lineHeight: 1, padding: '0 2px', flexShrink: 0, marginLeft: 4,
        // Telefoon: 32x32 tikgebied zonder de kaart hoger te maken dan nu (negatieve
        // marge trekt het omhoog/omlaag uitgerekte gebied weer in).
        ...(isMobile ? { minWidth: 32, minHeight: 32, marginTop: -7, marginBottom: -7, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' } : null),
      }}>×</button>
      {auto && <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2, borderRadius: '0 0 5px 5px', overflow: 'hidden' }}><div style={{ height: 2, background: HV2.borderStrong, animation: `pa-toast-prog ${auto}ms linear forwards` }} /></div>}
    </div>
  );
}

export default function Toaster() {
  const [items, setItems] = useState([]);
  const isMobile = useIsMobileViewport();
  // Meet de eigen stapelhoogte en publiceer die als CSS-variabele, zodat
  // TeamToastStack en VersionWatcher er op de telefoon boven kunnen stapelen
  // in plaats van op een vast getal dat overlapt zodra een kaart twee regels
  // wordt. Het element wordt in state bijgehouden (niet een kale ref) zodat de
  // observer in een effect leeft: die combinatie overleeft React's dubbele
  // effect-ronde, een observer die alleen in de ref-callback ontstaat niet.
  const [wrapEl, setWrapEl] = useState(null);
  const wrapRef = useCallback((el) => setWrapEl(el || null), []);
  useEffect(() => {
    const set = (px) => document.documentElement.style.setProperty('--pa-toaster-h', px);
    if (!isMobile || !wrapEl || typeof window === 'undefined' || typeof window.ResizeObserver !== 'function') {
      set('0px');
      return undefined;
    }
    const ro = new window.ResizeObserver((entries) => {
      const h = entries && entries[0] && entries[0].contentRect ? entries[0].contentRect.height : 0;
      set(`${h + 6}px`);
    });
    ro.observe(wrapEl);
    return () => { ro.disconnect(); set('0px'); };
  }, [isMobile, wrapEl]);
  useEffect(() => {
    const onToast = (t) => {
      // Cap the stack so persistent (error/warning) toasts can't pile up off-screen.
      setItems((prev) => [...prev, t].slice(-6));
      const auto = autoMs(t);
      if (auto) setTimeout(() => setItems((prev) => prev.filter((x) => x.id !== t.id)), auto);
    };
    listeners.add(onToast);
    return () => listeners.delete(onToast);
  }, []);
  const dismiss = (id) => setItems((prev) => prev.filter((x) => x.id !== id));
  if (!items.length) return null;
  // Phone: docked to the bottom (closest of the three stacks to the edge) so it
  // clears the header avatar/menu and the record close button up top; cards go
  // full width and their text wraps instead of overflowing. Desktop unchanged.
  const wrapStyle = isMobile
    ? { position: 'fixed', left: 12, right: 12, bottom: 'calc(12px + env(safe-area-inset-bottom))', zIndex: 10000, display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 6, maxHeight: 'calc(100vh - 24px)', overflowY: 'auto', pointerEvents: 'none' }
    : { position: 'fixed', top: 16, right: 16, zIndex: 10000, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, maxHeight: 'calc(100vh - 32px)', overflowY: 'auto', pointerEvents: 'none' };
  return (
    <div ref={wrapRef} data-pa-overlay="toast" style={wrapStyle}>
      {items.map((t) => <ToastCard key={t.id} t={t} onClose={() => dismiss(t.id)} isMobile={isMobile} />)}
      <style>{'@keyframes pa-toast-in{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}@keyframes pa-toast-prog{from{width:100%}to{width:0%}}'}</style>
    </div>
  );
}
